/* Catálogo de carros/skins. Carrega /cars/catalog.json em runtime — para adicionar um carro
 * de amigo basta soltar o arquivo em static/cars/ e adicionar uma linha no catalog.json
 * (sem rebuild). A rede só troca o ID do carro; cada navegador monta o visual localmente. */
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { makeProceduralBody } from './CarVisual.js'

export const DEFAULT_CAR = 'kart'

const BUILTIN = [
    { id: 'kart', name: 'Kart Clássico', owner: 'Carpano', type: 'procedural', style: 'kart' },
    { id: 'formula', name: 'Fórmula', owner: 'Carpano', type: 'procedural', style: 'formula' },
    { id: 'truck', name: 'Picape', owner: 'Carpano', type: 'procedural', style: 'truck' },
    // --- veículos do elenco ---
    { id: 'vital-convertible', name: 'Conversível do Vital', owner: 'Vital', type: 'procedural', style: 'convertible' },
    { id: 'gohan-classic', name: 'Clássico do Gohan', owner: 'Gohan', type: 'procedural', style: 'classic' },
    { id: 'artur-ship', name: 'Nave Neon do Artur', owner: 'Artur', type: 'procedural', style: 'neonship' },
]

class CarCatalog {
    constructor() {
        this.cars = new Map()
        BUILTIN.forEach((c) => this.cars.set(c.id, c))
        this.glbCache = new Map()
        this.loaded = false
        this._gltf = null
    }

    async load(url = '/cars/catalog.json') {
        try {
            const res = await fetch(url, { cache: 'no-store' })
            if (res.ok) {
                const data = await res.json()
                for (const c of data.cars || []) {
                    if (c && typeof c.id === 'string' && /^[\w-]{1,32}$/.test(c.id) &&
                        (c.type === 'procedural' || c.type === 'glb')) {
                        this.cars.set(c.id, { name: c.id, owner: '', ...c })
                    }
                }
            }
        } catch (_) { /* sem catálogo extra: só os carros padrão */ }
        this.loaded = true
        return this.list()
    }

    list() { return [...this.cars.values()] }
    has(id) { return this.cars.has(id) }
    get(id) { return this.cars.get(id) || this.cars.get(DEFAULT_CAR) }

    _loader() {
        if (!this._gltf) {
            const draco = new DRACOLoader()
            draco.setDecoderPath('/draco/')
            this._gltf = new GLTFLoader()
            this._gltf.setDRACOLoader(draco)
        }
        return this._gltf
    }

    _loadGlb(path) {
        if (!this.glbCache.has(path)) {
            this.glbCache.set(path, new Promise((resolve, reject) =>
                this._loader().load(path, (g) => resolve(g.scene), undefined, reject)))
        }
        return this.glbCache.get(path)
    }

    /** Retorna { group, tint[] } pronto pra encaixar no chassi. Nunca rejeita (cai no kart padrão). */
    async buildVisual(id, color = 0xFF0000) {
        const car = this.get(id)
        if (car.type === 'glb') {
            try {
                const scene = await this._loadGlb(car.model)
                return { group: this._fitGlb(scene.clone(true), car), tint: [] }
            } catch (err) {
                console.warn('[cars] falha ao carregar', car.id, err)
            }
        }
        const style = car.type === 'procedural' ? car.style : 'kart'
        return makeProceduralBody(style, color, car.type === 'procedural' ? car.skin : undefined)
    }

    // Auto-ajuste: modelos de amigos vêm em qualquer tamanho/origem.
    _fitGlb(model, car) {
        model.traverse((o) => { if (o.isMesh) o.castShadow = true })
        const wrap = new THREE.Group()
        wrap.add(model)
        model.rotation.y = THREE.MathUtils.degToRad(car.rotationY || 0)
        wrap.updateMatrixWorld(true)
        const box = new THREE.Box3().setFromObject(wrap)
        const size = box.getSize(new THREE.Vector3())
        const target = car.length || 3.6
        const s = (car.scale) || (target / Math.max(size.x, size.z, 0.001))
        model.scale.setScalar(s)
        wrap.updateMatrixWorld(true)
        box.setFromObject(wrap)
        const c = box.getCenter(new THREE.Vector3())
        const off = car.offset || [0, 0, 0]
        model.position.set(-c.x + off[0], (car.groundY ?? -0.45) - box.min.y + off[1], -c.z + off[2])
        return wrap
    }
}

export default new CarCatalog()
